import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowUpRight,
  AudioLines,
  ChevronDown,
  Disc3,
  Globe,
  Instagram,
  Music,
  Music2,
  Youtube,
} from 'lucide-react';

/** Platform keys usable in a credit's `profiles` list. */
export type PlatformKey =
  | 'instagram'
  | 'spotify'
  | 'appleMusic'
  | 'youtube'
  | 'soundcloud'
  | 'tiktok'
  | 'beatstars'
  | 'website';

export interface Profile {
  platform: PlatformKey;
  href: string;
  /** Optional override for the button text (defaults to the platform name) */
  label?: string;
}

export interface Credit {
  name: string;
  profiles?: Profile[];
}

/** Display name, icon and accent colour per platform. */
const PLATFORMS: Record<PlatformKey, { label: string; icon: React.ReactNode; accent: string }> = {
  instagram: { label: 'Instagram', icon: <Instagram className="h-4 w-4" />, accent: '236,72,153' },
  spotify: { label: 'Spotify', icon: <AudioLines className="h-4 w-4" />, accent: '34,197,94' },
  appleMusic: { label: 'Apple Music', icon: <Music className="h-4 w-4" />, accent: '244,63,94' },
  youtube: { label: 'YouTube', icon: <Youtube className="h-4 w-4" />, accent: '239,68,68' },
  soundcloud: { label: 'SoundCloud', icon: <Music2 className="h-4 w-4" />, accent: '249,115,22' },
  tiktok: { label: 'TikTok', icon: <Music2 className="h-4 w-4" />, accent: '56,189,248' },
  beatstars: { label: 'Beatstars', icon: <Disc3 className="h-4 w-4" />, accent: '250,204,21' },
  website: { label: 'Website', icon: <Globe className="h-4 w-4" />, accent: '148,163,184' },
};

const EASE = [0.16, 1, 0.3, 1] as const;

interface CreditTagsProps {
  credits: Credit[];
}

/**
 * Credit tags that expand into a profile menu on click. The menu sits in normal
 * flow, so opening it pushes the rest of the page down instead of covering it.
 * Closes on Escape, on outside click, or on a second click of the same tag.
 */
export const CreditTags: React.FC<CreditTagsProps> = ({ credits }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  const open = openIndex !== null ? credits[openIndex] : null;

  // Escape closes the menu
  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex]);

  // Clicking anywhere outside the tag row / menu closes it
  useEffect(() => {
    if (openIndex === null) return;
    const onDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenIndex(null);
      }
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [openIndex]);

  return (
    <div ref={containerRef} className="pt-1">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <span className="text-xs text-white/30">📀</span>

        {credits.map((credit, i) => {
          const hasProfiles = !!credit.profiles?.length;
          const isOpen = openIndex === i;

          const base =
            'group/tag relative inline-flex items-center gap-1.5 overflow-hidden rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-xl transition-all duration-300';

          // A credit with no profiles stays a plain, non-interactive tag
          if (!hasProfiles) {
            return (
              <span
                key={credit.name}
                className={`${base} cursor-default border-white/[0.08] bg-white/[0.04] text-white/50`}
              >
                {credit.name}
              </span>
            );
          }

          return (
            <motion.button
              key={credit.name}
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              aria-expanded={isOpen}
              aria-label={`${credit.name} profiles`}
              whileTap={reduced ? undefined : { scale: 0.94 }}
              className={`${base} ${
                isOpen
                  ? 'border-red-500/60 bg-red-950/45 text-red-200 shadow-[0_8px_24px_-8px_rgba(220,38,38,0.8)]'
                  : 'border-white/[0.08] bg-white/[0.04] text-white/50 hover:-translate-y-0.5 hover:border-red-500/40 hover:bg-red-950/30 hover:text-red-300 hover:shadow-[0_8px_20px_-8px_rgba(220,38,38,0.7)]'
              } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black`}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 -left-full w-1/2 skew-x-[-18deg] bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover/tag:animate-sweep"
              />
              <span className="relative">{credit.name}</span>
              <ChevronDown
                className={`relative h-3 w-3 shrink-0 transition-transform duration-300 ${
                  isOpen ? 'rotate-180' : 'group-hover/tag:translate-y-0.5'
                }`}
              />
            </motion.button>
          );
        })}
      </div>

      {/* Expanding profile menu — in flow, so siblings shift down */}
      <AnimatePresence initial={false} mode="wait">
        {open && (
          <motion.div
            key={open.name}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: reduced ? 0 : 0.45, ease: EASE },
              opacity: { duration: reduced ? 0 : 0.25 },
            }}
            className="overflow-hidden"
          >
            <motion.div
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: -10, filter: 'blur(8px)' }}
              animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8, filter: 'blur(6px)' }}
              transition={{ duration: reduced ? 0 : 0.4, ease: EASE, delay: reduced ? 0 : 0.06 }}
              className="relative mt-3 rounded-2xl border border-white/[0.10] bg-white/[0.045] p-3 backdrop-blur-2xl shadow-[0_24px_60px_-24px_rgba(220,38,38,0.6)]"
            >
              {/* Pointer notch + top hairline */}
              <span
                aria-hidden
                className="absolute -top-[5px] left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-l border-t border-white/[0.12] bg-[#120709]"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent"
              />

              <p className="mb-2 text-[0.6rem] font-semibold uppercase tracking-[0.22em] text-red-500/70">
                {open.name}
              </p>

              <div className="grid grid-cols-2 gap-2">
                {open.profiles!.map((profile, idx) => {
                  const meta = PLATFORMS[profile.platform];
                  return (
                    <motion.a
                      key={`${profile.platform}-${profile.href}`}
                      href={profile.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.96 }}
                      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        duration: reduced ? 0 : 0.4,
                        ease: EASE,
                        delay: reduced ? 0 : 0.12 + idx * 0.05,
                      }}
                      whileHover={reduced ? undefined : { y: -2 }}
                      whileTap={reduced ? undefined : { scale: 0.96 }}
                      style={{ ['--accent' as string]: meta.accent }}
                      className="group/p relative flex items-center gap-2 overflow-hidden rounded-xl border border-white/[0.08] bg-black/30 px-3 py-2.5 text-xs font-medium text-white/70 transition-colors duration-300 hover:border-[rgba(var(--accent),0.5)] hover:bg-[rgba(var(--accent),0.10)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                    >
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-y-0 -left-full w-1/2 skew-x-[-18deg] bg-gradient-to-r from-transparent via-white/15 to-transparent group-hover/p:animate-sweep"
                      />
                      <span className="relative text-[rgba(var(--accent),0.85)] transition-transform duration-300 group-hover/p:scale-110">
                        {meta.icon}
                      </span>
                      <span className="relative truncate">{profile.label ?? meta.label}</span>
                      <ArrowUpRight className="relative ml-auto h-3.5 w-3.5 shrink-0 opacity-0 transition-all duration-300 group-hover/p:translate-x-0.5 group-hover/p:-translate-y-0.5 group-hover/p:opacity-100" />
                    </motion.a>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreditTags;
